import { Button, Group, ScrollArea, Stack, Textarea } from '@mantine/core';
import { getHotkeyHandler } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Panel } from 'components/index';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useRef, useState } from 'react';
import type { useCreateInvoiceComment } from 'shared/hooks/use-create-invoice-comment';
import type { Comment } from 'shared/hooks/use-load-comments';

import { CommentItem } from './CommentItem';

type CommentsProps = {
  comments?: Array<Comment>;
  submitComment: ReturnType<typeof useCreateInvoiceComment>['createInvoiceComment'];
  resourceId: string | number;
  isCreateCommentPending: boolean;
};

export const Comments = ({
  comments = [],
  submitComment,
  resourceId,
  isCreateCommentPending,
}: CommentsProps) => {
  const { t } = useTranslation();

  const client = useQueryClient();

  const [commentInput, setCommentInput] = useState('');
  const [commentBeingEdited, setCommentBeingEdited] = useState<number>();

  const viewport = useRef<HTMLDivElement>(null);

  const scrollToBottom = () =>
    viewport.current!.scrollTo({ top: viewport.current!.scrollHeight, behavior: 'smooth' });

  useEffect(() => {
    scrollToBottom();
    return () => {};
  }, [comments.length]);

  const handleSubmitComment = () => {
    submitComment(
      {
        id: resourceId,
        comment: commentInput,
      },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ['comments'] });
        },
      },
    );
    setCommentInput('');
  };

  return (
    <Panel title={t(i18nKeys.NOTES)} withBorder>
      <ScrollArea.Autosize viewportRef={viewport} mah={500}>
        <Stack my="lg" mr="md">
          {comments.map(comment => (
            <CommentItem
              key={comment.id}
              comment={comment}
              isBeingEdited={commentBeingEdited === comment.id}
              isEditingDisabled={commentBeingEdited !== undefined}
              setIsBeingEdited={(isBeingEdited: boolean) => {
                setCommentBeingEdited(isBeingEdited ? comment.id : undefined);
              }}
            />
          ))}
        </Stack>
      </ScrollArea.Autosize>
      <Textarea
        value={commentInput}
        onChange={event => setCommentInput(event.currentTarget.value)}
        placeholder={t(i18nKeys.FORM.ADD_COMMENT)}
        pr="md"
        styles={{ input: { height: '80px' } }}
        onKeyDown={getHotkeyHandler([['mod+Enter', handleSubmitComment]])}
      />
      <Group justify="flex-end" pt="sm" pr="md">
        <Button
          variant="light"
          disabled={commentInput === ''}
          onClick={handleSubmitComment}
          loading={isCreateCommentPending}
        >
          {t(i18nKeys.FORM.ADD_COMMENT)}
        </Button>
      </Group>
    </Panel>
  );
};
